import type { CaptureResult, PostHog, Properties } from "posthog-js"
import type { AnalyticsBrowserConfig } from "@/types/inertia"

type AnalyticsConsent = "granted" | "denied" | null

const CONSENT_KEY = "xplore-analytics-consent"
const CONSENT_CHANGED_EVENT = "xplore:analytics-consent-changed"
export const OPEN_ANALYTICS_PREFERENCES_EVENT = "xplore:open-analytics-preferences"
const SCROLL_MILESTONES = [25, 50, 75, 90] as const

let analyticsConfig: AnalyticsBrowserConfig | null = null
let posthog: PostHog | null = null
let initialization: Promise<PostHog> | null = null
let inMemoryConsent: AnalyticsConsent = null
let currentComponent = ""
let currentPath = ""
let scrollFrame: number | null = null
let reachedScrollMilestones = new Set<number>()

export function setupAnalytics(config: AnalyticsBrowserConfig | null, component: string) {
  analyticsConfig = config
  currentComponent = component
  currentPath = window.location.pathname

  if (!analyticsConfig) return

  window.addEventListener("scroll", scheduleScrollMeasurement, { passive: true })
  window.addEventListener("resize", scheduleScrollMeasurement, { passive: true })

  if (getAnalyticsConsent() === "granted" && isPublicAnalyticsPath(currentPath)) {
    void activateAnalyticsForCurrentPage()
  }
}

export function trackAnalyticsPage(component: string) {
  currentComponent = component
  currentPath = window.location.pathname
  reachedScrollMilestones = new Set()

  if (!analyticsConfig || getAnalyticsConsent() !== "granted") return

  if (!isPublicAnalyticsPath(currentPath)) {
    if (posthog) {
      posthog.stopSessionRecording()
      posthog.opt_out_capturing()
    }
    return
  }

  void activateAnalyticsForCurrentPage()
}

export function captureAnalyticsEvent(name: string, properties: Properties = {}) {
  if (!canCapture() || !posthog) return

  posthog.capture(name, {
    ...pageProperties(),
    ...properties,
  })
}

export function getAnalyticsConsent(): AnalyticsConsent {
  if (inMemoryConsent !== null) return inMemoryConsent

  try {
    const stored = window.localStorage.getItem(CONSENT_KEY)
    inMemoryConsent = stored === "granted" || stored === "denied" ? stored : null
    return inMemoryConsent
  } catch {
    return null
  }
}

export function setAnalyticsConsent(granted: boolean) {
  const consent: Exclude<AnalyticsConsent, null> = granted ? "granted" : "denied"
  inMemoryConsent = consent

  try {
    window.localStorage.setItem(CONSENT_KEY, consent)
  } catch {
    // The in-memory choice still applies for this page when storage is unavailable.
  }

  if (granted && analyticsConfig && isPublicAnalyticsPath(window.location.pathname)) {
    void activateAnalyticsForCurrentPage("analytics_consent_granted")
  } else if (posthog) {
    posthog.stopSessionRecording()
    posthog.opt_out_capturing()
  }

  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: consent }))
}

export function onAnalyticsConsentChange(listener: (consent: AnalyticsConsent) => void) {
  const handleChange = (event: Event) => {
    listener((event as CustomEvent<AnalyticsConsent>).detail)
  }

  window.addEventListener(CONSENT_CHANGED_EVENT, handleChange)
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handleChange)
}

async function initializePostHog() {
  if (posthog) return posthog
  if (initialization) return initialization
  if (!analyticsConfig) throw new Error("PostHog cannot initialize without analytics configuration")

  const config = analyticsConfig
  initialization = import("posthog-js").then(({ default: posthogInstance }) => {
    posthogInstance.init(config.posthog_key, {
      api_host: config.posthog_host,
      ui_host: config.posthog_host.includes("eu.") ? "https://eu.posthog.com" : "https://us.posthog.com",
      autocapture: {
        url_allowlist: [ `${window.location.origin}/en`, `${window.location.origin}/de` ],
      },
      capture_dead_clicks: true,
      capture_heatmaps: true,
      capture_pageleave: true,
      capture_pageview: false,
      capture_performance: {
        network_timing: false,
        web_vitals: true,
      },
      custom_personal_data_properties: [ "email", "q", "token" ],
      disable_capture_url_hashes: true,
      enable_recording_console_log: false,
      mask_personal_data_properties: true,
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      persistence: "localStorage",
      person_profiles: "never",
      session_recording: {
        blockSelector: "[data-analytics-private]",
        maskAllInputs: true,
        maskTextSelector: "[data-private]",
        recordBody: false,
        recordHeaders: false,
      },
      before_send: sanitizeCapturedEvent,
    })

    posthog = posthogInstance
    return posthogInstance
  })

  return initialization
}

async function activateAnalyticsForCurrentPage(consentEvent: string | false = false) {
  const instance = await initializePostHog()
  if (getAnalyticsConsent() !== "granted" || !isPublicAnalyticsPath(window.location.pathname)) return

  instance.opt_in_capturing({ captureEventName: consentEvent })
  instance.startSessionRecording()
  capturePageView()
  scheduleScrollMeasurement()
}

function capturePageView() {
  if (!canCapture() || !posthog) return

  posthog.capture("$pageview", {
    ...pageProperties(),
    $current_url: `${window.location.origin}${window.location.pathname}`,
  })
}

function pageProperties(): Properties {
  return {
    locale: window.location.pathname.split("/")[1] || null,
    page_name: currentComponent,
    pathname: window.location.pathname,
  }
}

function canCapture() {
  return posthog !== null && getAnalyticsConsent() === "granted" && isPublicAnalyticsPath(window.location.pathname)
}

function isPublicAnalyticsPath(pathname: string) {
  return /^\/(en|de)(?:\/|$)/.test(pathname)
}

function scheduleScrollMeasurement() {
  if (scrollFrame !== null) return

  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = null
    measureScrollDepth()
  })
}

function measureScrollDepth() {
  if (!canCapture() || currentPath !== window.location.pathname) return

  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight
  if (scrollableHeight <= 0) return

  const depth = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100))

  for (const milestone of SCROLL_MILESTONES) {
    if (depth < milestone || reachedScrollMilestones.has(milestone)) continue

    reachedScrollMilestones.add(milestone)
    captureAnalyticsEvent("scroll_depth_reached", { depth_percent: milestone })
  }
}

function sanitizeCapturedEvent(event: CaptureResult | null) {
  if (!event?.properties) return event

  for (const property of [ "$current_url", "$initial_current_url", "$session_entry_url" ]) {
    const value = event.properties[property]
    if (typeof value === "string") event.properties[property] = sanitizedUrl(value)
  }

  return event
}

function sanitizedUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin)
    const allowedParameters = [ "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term" ]
    const sanitized = new URL(`${url.origin}${url.pathname}`)

    for (const parameter of allowedParameters) {
      const parameterValue = url.searchParams.get(parameter)
      if (parameterValue) sanitized.searchParams.set(parameter, parameterValue)
    }

    return sanitized.toString()
  } catch {
    return value.split("?")[0].split("#")[0]
  }
}
